export interface CustomerInfo {
  firstName: string;
  lastName: string;
  email: string;
  isOpPlusMember: boolean;
}

type SubscriptionStatus =
  | 'active'
  | 'withdrawn'
  | 'non-renewing'
  | 'cancelled'
  | 'blocked'
  | 'expired'
  | 'deleted'
  | 'failed-payment-paused'
  | 'failed-payment-cancelled';

export interface SubscriptionInfo {
  isOpPlusSubscriptionMember: boolean;
  isActiveOpPlusMember: boolean;
  startDate: string;
  renewalDate: string;
  joinDate: string;
  endDate: string;
  cancelDate?: any;
  startDateFormatted: string;
  joinDateFormatted: string;
  renewalDateFormatted: string;
  endDateFormatted: string;
  cancelDateFormatted: string;
  paymentMethod: string;
  status: SubscriptionStatus;
  plusPrice: number;
  renewalFailedDate?: string;
}

export interface SubscriptionStatistics {
  totalOrders: number;
  savedAmount: string;
  lastOrderSavings: number;
  last12MonthSavings: number;
}

export interface Url {
  learnMoreUrl: string;
  opPlusLogoUrl: string;
  txtContactCsUrl: string;
  opPlusShortLogoUrl: string;
}

export interface Translation {
  helloTxt: string;
  subscriptionDetailsTxt: string;
  subscriptionStartDateTxt: string;
  subscriptionRenewalDateTxt: string;
  paymentMethodTxt: string;
  yourSubscriptionTxt: string;
  totalOrdersTxt: string;
  savedAmountTxt: string;
  remarkTxt: string;
  cancelSubscriptionTxt: string;
  learnMoreTxt: string;
  txtContactCs: string;
  cancelledAccountMsg: string;
  membershipEndDate: string;
  legacyMsgLine1: string;
  legacyMsgLine2: string;
  legacySignUpButton: string;
  legacySignUpForButton: string;
  opPlusPricePer: string;
}

export interface Info {
  customerFirstName: string;
  customerLastName: string;
  customerFullName: string;
  expirationDays: number;
  opPlusLogo: string;
  learnMoreUrl: string;
  savingsLastYear: number;
  savingsLastYearFormatted: string;
  opPlusEndDate: string;
  opPlusEndDateFormatted: string;
  nextMembershipStartDate: string;
  nextMembershipStartDateFormatted: string;
  nextMembershipRenewalDate: string;
  nextMembershipRenewalDateFormatted: string;
  opPlusLogoCompact: string;
  price: {
    value: string;
    currency: string;
    formatted: string;
  };
}

export interface Announcement {
  greeting: string;
  line1: string;
  line2: string;
  signUp: string;
  learnMore: string;
  muteAnnouncementHours: number;
}

export interface StickyBar {
  description: string;
  learnMore: string;
}

export interface Reminder {
  freeDeliveryLabel: string;
  line1: string;
  line2: string;
  learnMore: string;
  signUp: string;
}

export interface UpSell {
  title: string;
  description: string;
  timelineLine1: string;
  timelineLine2: string;
  timelineLine3: string;
  signUp: string;
  learnMore: string;
}

export interface LegacyMemberNotification {
  displayNotification: boolean;
  info: Info;
  announcement: Announcement;
  stickyBar: StickyBar;
  reminder: Reminder;
  upSell: UpSell;
  translation: {
    addingPlusMsgLong: string;
    addingPlusMsgShort: string;
  };
}

export interface NonPlusInfo {
  addPlusButtonTxt: string;
  benefits: string[];
  description: string;
  freeDeliveryTxt: string;
  nonMemberPrice: string;
  nonMemberPriceTxt: string;
  title: string;
}

export interface SubscriptionData {
  isDisplayed: boolean;
  customerInfo: CustomerInfo;
  subscriptionInfo: SubscriptionInfo;
  subscriptionStatistics: SubscriptionStatistics;
  url: Url;
  translation: Translation;
  nonPlusInfo?: NonPlusInfo;
  showCancellationButton: boolean;
  contentType: string;
  legacyMemberNotification: LegacyMemberNotification;
}

export interface OpPlusV3Info {
  isDisplayed: boolean;
  customerInfo: CustomerInfo;
  subscriptionInfo: SubscriptionInfo;
  subscriptionStatistics: SubscriptionStatistics;
  url: Url;
  nonPlusInfo?: NonPlusInfo;
  showCancellationButton: boolean;
  contentType: string;
  legacyMemberNotification: LegacyMemberNotification;
}

export enum CancellationFlowStep {
  initial = 'Initial',
  confirmation = 'Confirmation',
  reasons = 'Reasons',
  cancelled = 'Cancelled',
}

export interface ReasonType {
  key: string;
  option: string;
}

export interface CancellationReasonsType {
  isOpPlusMember: boolean;
  txtBack: string;
  title: string;
  subtTitle: string;
  reasonsOptions: ReasonType[];
  helpTextPlaceHolder: string;
  cancelButton: string;
  keepButton: string;
  txtOptionsTitle: string;
  txtOptionsNeedHelp: string;
  txtContactCs: string;
  txtContactCsUrl: string;
}

export interface Benefit {
  imageUrl: string;
  description: string;
}

export interface Review {
  content: string;
  customerInfo: string;
  customerPhotoUrl: string;
}

export interface CancellationConfirmationPageType {
  isOpPlusMember: boolean;
  opPlusLogoUrl: string;
  txtBack: string;
  txtTitle: string;
  txtMemberSince: string;
  memberSinceDate: string;
  txtTotalOrders: string;
  totalOrders: string;
  txtMoneySaved: string;
  moneySaved: string;
  txtLoseBenefits: string;
  benefits: Benefit[];
  txtReviewTitle: string;
  reviews: Review[];
  txtPolicyTitle: string;
  txtPolicyDescription: string;
  txtOptionsTitle: string;
  txtOptionsNeedHelp: string;
  txtContactCs: string;
  txtContactCsUrl: string;
  txtYesCancelButton: string;
  txtNoKeepButton: string;
}

export interface CancellationResultType {
  isCancelled: boolean;
  endDate: string;
  email: string;
  title: string;
  description: string;
  confirmationEmailMsg: string;
  txtDoneButton: string;
}
